import React, { useRef, useEffect } from "react";
import { Box, TextField, IconButton, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Icon-triggered search — collapsed to a single search IconButton until
 * clicked, then expands into a full-width text field with a clear/close (X)
 * button that reverts back to the icon and clears whatever was typed.
 * Shared by the Chats (InboxAll.jsx) and Calls (CallsSectionBody.jsx)
 * sidebar headers so both get the same interaction, not two forks of it.
 *
 * `onSubmit` (2026-09-08 change): fired on Enter -- the real backend search
 * used to fire on a 400ms typing-pause debounce, which on a large account
 * meant a real DB-hitting request per partial word typed (natural pauses
 * between letters/words each re-fire it), not just once at the end.
 * Optional: Calls search is local-only, so it has nothing to submit.
 *
 * A loading spinner briefly lived in this box's own end-adornment (same
 * 2026-09-08 change) but was moved into MessageCard.jsx's own centered
 * empty-state spot instead (Rahim: didn't like it here) -- see that file's
 * `isSearching` prop for the current loading UI.
 */
export default function InboxSearchToggle({ value, onChange, open, onOpenChange, placeholder = "Search", iconSize = 20, onSubmit }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const close = () => {
    onOpenChange(false);
    if (value) onChange("");
  };

  if (!open) {
    return (
      <IconButton
        onClick={() => onOpenChange(true)}
        aria-label="Search"
        sx={{
          color: (theme) => theme.palette.text.secondary,
          flexShrink: 0,
          // MUI's default IconButton padding (medium size) made this button's
          // own box noticeably taller than the 30px Chats/Calls Tabs it sits
          // beside — even with equal alignItems:center + margin, the two
          // never actually LOOKED aligned since their real box heights
          // differed. Constrained to match (Rahim caught this live,
          // 2026-08-20).
          width: 30,
          height: 30,
          padding: 0,
        }}
      >
        <SearchIcon sx={{ fontSize: iconSize }} />
      </IconButton>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5 }}>
      <TextField
        inputRef={inputRef}
        placeholder={placeholder}
        variant="outlined"
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") close();
          if (e.key === "Enter") onSubmit?.();
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={close} aria-label="Close search">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            // Rectangle with a cut corner, matching this same row's own
            // "Unreplied" button radius (2026-09-08, Rahim: not a pill) --
            // was 20px (fully rounded).
            borderRadius: "8px",
            // Focus border was MUI's default primary-color (green) outline —
            // swapped for the app's own standard grey token (text.secondary,
            // same one used for dividers/muted text everywhere else), not
            // text.primary — that read as too dark/harsh (Rahim, 2026-08-20).
            "&.Mui-focused fieldset": { borderColor: (theme) => theme.palette.text.secondary },
          },
        }}
      />
    </Box>
  );
}
